import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table, Button, Space, App, Typography, Select, DatePicker, Tag, Tooltip,
} from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  getAvailableScenes, batchAddScenesToTag, getSceneLabels, getSceneStatusList,
  getSceneTagDetail,
  AvailableSceneItem, StatusOption,
  type SceneSource,
} from '@apis/simulation';
import { SceneStatusTag } from '@components/StatusTag';
import SceneSourceTag, { SCENE_SOURCE_FILTER_OPTIONS } from '@components/SceneSourceTag';
import { useEnterToSearch } from '@/common/keyboard';
import { formatBatchAddResult } from '@/utils/sceneSetAddResult';

const TOKEN_SEPARATORS = [',', ' ', '，', ';', '；'];

const cleanTags = (tags: string[] | undefined): string[] | undefined => {
  if (!tags?.length) return undefined;
  const cleaned = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
  return cleaned.length ? cleaned : undefined;
};

const cleanIdTags = (tags: string[] | undefined): number[] | undefined => {
  const cleaned = cleanTags(tags);
  if (!cleaned) return undefined;
  const nums = cleaned
    .map(t => Number(t))
    .filter(n => Number.isFinite(n) && Number.isInteger(n));
  return nums.length ? nums : undefined;
};

const { Text, Title } = Typography;

const formatTime = (val: string | null | undefined) =>
  val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-';

export default function AddSceneToTagPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { tagId } = useParams<{ tagId: string }>();
  const [searchParams] = useSearchParams();
  const tagName = searchParams.get('name') || '';
  const tagIdNum = Number(tagId);

  const [tagPath, setTagPath] = useState<string>(tagName);

  const [data, setData] = useState<AvailableSceneItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [filterIds, setFilterIds] = useState<string[]>([]);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterJiras, setFilterJiras] = useState<string[]>([]);
  const [filterLabels, setFilterLabels] = useState<string[]>([]);
  const [filterSources, setFilterSources] = useState<string[]>([]);
  const [filterRegions, setFilterRegions] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<number | undefined>(undefined);
  const [filterCreateTimeRange, setFilterCreateTimeRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [labelOptions, setLabelOptions] = useState<string[]>([]);
  const [labelLoading, setLabelLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const AVAILABLE_STATUS_IDS = [5, 7, 8];

  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  const toIso = (d: Dayjs | null | undefined): string | undefined => {
    if (!d || !d.isValid()) return undefined;
    return d.format('YYYY-MM-DDTHH:mm:ss');
  };

  const fetchData = useCallback(async (
    current = 1,
    pageSize = 20,
    overrides?: {
      ids?: string[]; names?: string[]; jiras?: string[]; labels?: string[]; sources?: string[];
      regions?: string[];
      status?: number | null;
      createTimeRange?: [Dayjs, Dayjs] | null;
    },
  ) => {
    const ids = overrides?.ids ?? filterIds;
    const names = overrides?.names ?? filterNames;
    const jiras = overrides?.jiras ?? filterJiras;
    const labels = overrides?.labels ?? filterLabels;
    const sources = overrides?.sources ?? filterSources;
    const regions = overrides?.regions ?? filterRegions;
    const status = overrides && 'status' in overrides
      ? (overrides.status ?? undefined)
      : filterStatus;
    const createTimeRange = overrides && 'createTimeRange' in overrides
      ? overrides.createTimeRange
      : filterCreateTimeRange;
    setLoading(true);
    try {
      const idsArr = cleanIdTags(ids);
      const namesArr = cleanTags(names);
      const jirasArr = cleanTags(jiras);
      const labelsArr = cleanTags(labels);
      const sourcesArr = cleanTags(sources);
      const regionsArr = (cleanTags(regions) ?? [])
        .map(r => r.toLowerCase())
        .filter(r => ['cn', 'ae', 'sea'].includes(r));
      const ctsIso = createTimeRange ? toIso(createTimeRange[0]) : undefined;
      const cteIso = createTimeRange ? toIso(createTimeRange[1]) : undefined;
      const resp = await getAvailableScenes({
        current,
        pageSize,
        ...(idsArr ? { ids: idsArr } : {}),
        ...(namesArr ? { names: namesArr } : {}),
        ...(jirasArr ? { jiraIssueCodes: jirasArr } : {}),
        ...(labelsArr ? { labels: labelsArr } : {}),
        ...(sourcesArr ? { sources: sourcesArr as SceneSource[] } : {}),
        ...(regionsArr.length ? { regions: regionsArr } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(ctsIso ? { createTimeStart: ctsIso } : {}),
        ...(cteIso ? { createTimeEnd: cteIso } : {}),
        includeDisabled: true,
      });
      setData(resp.list);
      setPagination({
        current: resp.page?.current ?? current,
        pageSize: resp.page?.pageSize ?? pageSize,
        total: resp.page?.totalElements ?? resp.total,
      });
    } finally {
      setLoading(false);
    }
  }, [filterIds, filterNames, filterJiras, filterLabels, filterSources, filterRegions, filterStatus, filterCreateTimeRange]);

  const loadLabelOptions = useCallback(async () => {
    setLabelLoading(true);
    try {
      const labels = await getSceneLabels();
      setLabelOptions(labels);
    } catch { /* interceptor handles */ } finally {
      setLabelLoading(false);
    }
  }, []);

  const loadStatusOptions = useCallback(async () => {
    try {
      const all = await getSceneStatusList();
      setStatusOptions(
        all.filter(o => AVAILABLE_STATUS_IDS.includes(Number(o.value))),
      );
    } catch { /* interceptor handles */ }
  }, []);

  useEffect(() => {
    fetchData(1, 20);
    loadLabelOptions();
    loadStatusOptions();
    if (tagIdNum && Number.isInteger(tagIdNum)) {
      getSceneTagDetail(tagIdNum)
        .then((d) => setTagPath(d.tagPath))
        .catch(() => { /* 失败沿用 query 参数名 */ });
    }
  }, [fetchData, loadLabelOptions, loadStatusOptions, tagIdNum]);

  const handleSearch = () => {
    setSelectedRowKeys([]);
    fetchData(1, pagination.pageSize);
  };

  const enterToSearch = useEnterToSearch(handleSearch);

  const handleReset = () => {
    setFilterIds([]);
    setFilterNames([]);
    setFilterJiras([]);
    setFilterLabels([]);
    setFilterSources([]);
    setFilterRegions([]);
    setFilterStatus(undefined);
    setFilterCreateTimeRange(null);
    setSelectedRowKeys([]);
    fetchData(1, pagination.pageSize, {
      ids: [], names: [], jiras: [], labels: [], sources: [], regions: [],
      status: null,
      createTimeRange: null,
    });
  };

  const handleConfirm = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一个场景');
      return;
    }
    setSubmitting(true);
    try {
      const result = await batchAddScenesToTag(tagIdNum, selectedRowKeys as number[]);
      const { type, text } = formatBatchAddResult(result);
      message[type](`添加完成：${text}`);
      navigate(-1);
    } catch (e) {
      console.error('添加场景到标签失败:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '场景名称',
      dataIndex: 'name',
      width: 220,
      ellipsis: true,
      render: (name: string, record: AvailableSceneItem) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {name}
          {record.isDeleted ? (
            <Tooltip title="可见但运行任务被过滤">
              <Tag className="scene-disabled-tag" style={{ cursor: 'help' }}>已禁用</Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      width: 140,
      ellipsis: true,
      render: (v: string | null | undefined) => v || '-',
    },
    {
      title: '标签',
      dataIndex: 'label',
      width: 120,
      ellipsis: true,
      render: (v: string | null | undefined) => v || '-',
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 110,
      render: (v: string | null | undefined) => <SceneSourceTag source={v} />,
    },
    {
      title: '状态', dataIndex: 'statusName', width: 110,
      render: (_: string, record: AvailableSceneItem) => (
        <SceneStatusTag statusName={record.statusName} />
      ),
    },
    { title: 'record 地址', dataIndex: 'recordAddress', width: 200, ellipsis: true },
    { title: 'VIN', dataIndex: 'vin', width: 120, ellipsis: true },
    { title: '创建人', dataIndex: 'creator', width: 100 },
    { title: '创建时间', dataIndex: 'createTime', width: 180, render: formatTime },
    { title: '地图', dataIndex: 'mapName', width: 120 },
    { title: '版本', dataIndex: 'mapVersion', width: 100 },
    { title: '场景类型', dataIndex: 'sceneType', width: 120 },
  ];

  return (
    <div style={{ padding: 24, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>返回</Button>
          <Title level={5} style={{ margin: 0 }}>
            添加场景到标签「{tagPath}」
          </Title>
        </Space>
        <Space>
          <Text type="secondary">已选 {selectedRowKeys.length} 条</Text>
          <Button
            type="primary"
            loading={submitting}
            disabled={!selectedRowKeys.length}
            onClick={handleConfirm}
          >
            确定添加
          </Button>
        </Space>
      </div>

      <div style={{
        padding: '12px 16px', background: '#fff', borderRadius: 8,
        border: '1px solid #f0f0f0', marginBottom: 16,
      }}>
        <Space wrap>
          <Select
            mode="tags"
            placeholder="场景 ID（可多个，回车确认）"
            value={filterIds}
            onChange={(vals: string[]) => setFilterIds(
              vals.map(v => v.trim()).filter(v => v && /^\d+$/.test(v)),
            )}
            {...enterToSearch(1)}
            tokenSeparators={TOKEN_SEPARATORS}
            style={{ minWidth: 220 }}
          />
          <Select
            mode="tags"
            placeholder="场景名称（可多个，回车确认）"
            value={filterNames}
            onChange={(vals: string[]) => setFilterNames(vals.map(v => v.trim()).filter(Boolean))}
            {...enterToSearch(2)}
            tokenSeparators={TOKEN_SEPARATORS}
            style={{ minWidth: 240 }}
          />
          <Select
            mode="tags"
            placeholder="Jira / Issue（可多个，回车确认）"
            value={filterJiras}
            onChange={(vals: string[]) => setFilterJiras(vals.map(v => v.trim()).filter(Boolean))}
            {...enterToSearch(3)}
            tokenSeparators={TOKEN_SEPARATORS}
            style={{ minWidth: 240 }}
          />
          <Select
            mode="multiple"
            placeholder="标签（可多选）"
            value={filterLabels}
            onChange={(vals: string[]) => setFilterLabels(vals)}
            {...enterToSearch(4)}
            allowClear
            showSearch
            loading={labelLoading}
            maxTagCount="responsive"
            style={{ minWidth: 220 }}
            options={labelOptions.map(l => ({ label: l, value: l }))}
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Select
            mode="multiple"
            placeholder="来源（可多选）"
            value={filterSources}
            onChange={(vals: string[]) => setFilterSources(vals)}
            {...enterToSearch(5)}
            options={SCENE_SOURCE_FILTER_OPTIONS}
            allowClear
            maxTagCount="responsive"
            style={{ minWidth: 180 }}
          />
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 160 }}
            value={filterStatus}
            onChange={v => setFilterStatus(v ?? undefined)}
            {...enterToSearch(6)}
            options={statusOptions}
          />
          <Select
            mode="multiple"
            placeholder="区域（可多选）"
            value={filterRegions}
            onChange={(vals: string[]) => setFilterRegions(vals)}
            {...enterToSearch(7)}
            options={[
              { value: 'cn', label: '国内' },
              { value: 'ae', label: '阿联酋' },
              { value: 'sea', label: '新加坡' },
            ]}
            allowClear
            maxTagCount="responsive"
            style={{ minWidth: 160 }}
          />
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm:ss' }}
            format="YYYY-MM-DD HH:mm:ss"
            placeholder={['创建开始时间', '创建结束时间']}
            value={filterCreateTimeRange}
            onChange={(range) => {
              if (!range || !range[0] || !range[1]) {
                setFilterCreateTimeRange(null);
              } else {
                setFilterCreateTimeRange([range[0], range[1]]);
              }
            }}
            style={{ minWidth: 380 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      <div ref={tableScrollRef} style={{
        flex: 1, background: '#fff', borderRadius: 8,
        border: '1px solid #f0f0f0', overflow: 'auto',
      }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          size="small"
          scroll={{ x: 1400 }}
          sticky={{ getContainer: () => tableScrollRef.current ?? window }}
          rowClassName={(r) => (r.isDeleted ? 'scene-row-disabled' : '')}
          rowSelection={{
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys),
            preserveSelectedRowKeys: true,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '1000'],
            showTotal: t => `共 ${t} 条可用场景`,
            onChange: (page, size) => {
              fetchData(page, size);
            },
          }}
        />
      </div>
    </div>
  );
}